import {Component, OnDestroy, OnInit} from '@angular/core';
import {AuthService, LoginResult} from '../../core/auth/auth.service';
import {ActivatedRoute, Router} from '@angular/router';
import {Language} from '@app/core/constants/language';
import {TranslateService} from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import {NGXLogger} from 'ngx-logger';
import {CookieService} from '@app/core/services/cookie.service';
import {query} from '@angular/animations';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  public LoginError = LoginError;
  public Language = Language;

  /** Emits true if user is logged out, so we are ready for login */
  private readonly logoutFinishedSubject = new BehaviorSubject(false);

  public isLoadingLogin = false;
  public showCreateNotice = false;

  public isLoginV2 = false;
  public savePassword = false;

  public isIos = false;
  public isIPad = false;
  public isIosAlone = false;

  public isParentsLogin = false;

  credentials: Credentials = {
    username: '',
    password: ''
  };

  loginError = LoginError.None;

  public get language(): Language {
    return this.auth.getCurrentLanguage();
  }
  public set language(value: Language) {
    this.auth.switchLanguage(value);
  }

  constructor(private auth: AuthService,
              private log: NGXLogger,
              private translate: TranslateService,
              private route: ActivatedRoute,
              private cookie: CookieService,
              private router: Router) { }

  onLogin() {

    // First of all delete our report "markers"
    this.auth.deleteLocalStorageReports();
    this.isLoadingLogin = true;


    // Wait until we get the "go" from logout (if no logout was required, we instantly call login)
    this.logoutFinishedSubject.pipe(
      filter(finished => finished === true),
      switchMap(() => this.auth.login(this.credentials.username, this.credentials.password))
    ).subscribe({
      next: (result) => {
        if (result === LoginResult.Success) {
          this.isLoadingLogin = false;
          if (this.isLoginV2 || this.isParentsLogin) this.handleLoginV2();
          this.loginError = LoginError.None;
          if (this.isParentsLogin) {
            this.auth.isParentsApp = true;
            this.router.navigate(['parents']);
          } else {
            this.auth.isParentsApp = false;
            this.router.navigate([this.auth.loginRedirectUrl]);
          }
        } else {
          this.isLoadingLogin = false;
          if (result === LoginResult.Http401) {
            this.loginError = LoginError.IncorrectCredentials;
          } else if (result === LoginResult.HttpForbidden) {
            this.loginError = LoginError.Forbidden;
            } else
          {
            this.loginError = LoginError.OtherError;
          }
        }
      }
    });
  }

  ngOnInit(): void {
    /*
     * Call logout if user is already logged in.
     * Don't keep user logged in because we don't want to prevent the user from logging in with default role
     * if user was logged in with report/parents role before.
     * This especially holds e.g. if a user is using the app for parents view (parents role), the user closes the app
     * (i.e. the app could not call logout) and now the user opens the app again and wants to login with default role.
     */

    this.checkforIos();

    if (this.auth.isLoggedIn) {
      this.auth.logoutObserved(false).subscribe(() => {
        this.logoutFinishedSubject.next(true);
      });
    } else {
      this.logoutFinishedSubject.next(true);
    }

    this.route.queryParams.subscribe(queryParams => {
      if (queryParams.v === '2') {
        this.initV2Login();
      }
      if (queryParams.redirect === 'parents') {
        this.auth.isParentsApp = true;
        this.isParentsLogin = true;
      }
    });

    // We are coming from a logout screen but loginV2 is still true in localStorage
    if (this.isLoginV2 === false && localStorage?.getItem('loginv2') === 'true') {
      this.initV2Login();
    }

    // Maybe we have isParentsApp in localstorage?
    this.isParentsLogin = this.auth.isParentsApp;

    if (this.isParentsLogin) {
      this.getSavedCredentials();
    }

  }

  public ngOnDestroy() {
    this.logoutFinishedSubject.complete();
  }

  private initV2Login() {
    localStorage?.setItem('loginv2', 'true');
    this.isLoginV2 = true;
    this.getSavedCredentials();
  }

  private getSavedCredentials() {
    this.credentials.username = localStorage?.getItem('username') ?? '';
    this.credentials.password = localStorage?.getItem('password') ?? '';
    this.savePassword = (localStorage?.getItem('savepassword') === 'true');
  }

  onCreateAccountClick() {
    this.showCreateNotice = !this.showCreateNotice;
  }

  checkforIos() {
    this.isIos = navigator.userAgent.toLowerCase().indexOf('iphone;') > 0;
    this.isIPad = navigator.userAgent.toLowerCase().indexOf('ipad;') > 0;
    if (this.isIPad) this.isIos = true;

    let winNavigator: any;
    winNavigator = window.navigator;    // workaround because standalone is missing in window.navigator
    this.isIosAlone = ('standalone' in window.navigator) && (winNavigator.standalone);

    this.log.info('PWA check: isIOS=' + this.isIos + '|isStandalone=' + this.isIosAlone);
  }

  closeIosPopup() {
    this.isIos = false;
  }

  handleLoginV2() {
    if (this.savePassword === true) {
      this.savePasswordLS();
    } else {
      this.deleteLS();
    }
  }

  savePasswordLS() {
    localStorage?.setItem('username', this.credentials.username);
    localStorage?.setItem('password', this.credentials.password);
    localStorage?.setItem('savepassword', 'true');
  }

  deleteLS() {
    localStorage?.removeItem('username');
    localStorage?.removeItem('password');
    localStorage?.removeItem('savepassword');
  }

  toggleParentsApp() {
    this.auth.isParentsApp = !this.auth.isParentsApp;
    this.isParentsLogin = this.auth.isParentsApp;
  }

  isParentsApp() {
    return this.auth.isParentsApp;
  }


}

interface Credentials {
  username: string;
  password: string;
}

export enum LoginError {
  None,
  IncorrectCredentials,
  OtherError,
  Forbidden
}
